// Чистая логика экранов сна и активностей: подписи, зоны пульса, фазы сна, ряд пульса.

import { stepValueAt, type SeriesPoint } from '@/components/charts/prepare'
import { formatNumber } from '@/lib/format'

import type { Activity, Sleep } from './api'

export const SPORT_LABELS: Record<string, string> = {
  run: 'Бег',
  bike: 'Велосипед',
  walk: 'Ходьба',
  swim: 'Плавание',
  strength: 'Силовая',
  cardio: 'Кардио',
  other: 'Другое',
}

export const sportLabel = (sport: string) => SPORT_LABELS[sport] ?? SPORT_LABELS.other!

export function activityTitle(a: Pick<Activity, 'name' | 'sport'>): string {
  return a.name?.trim() || sportLabel(a.sport)
}

export type ZoneRow = { zone: number; seconds: number; percent: number }

/** Время в зонах 1–5 и доля от суммы (проценты, целые). Нет данных — пустой список. */
export function zoneRows(zones: number[] | null | undefined): ZoneRow[] {
  if (!zones || zones.length === 0) return []
  const values = Array.from({ length: 5 }, (_, i) => Math.max(0, zones[i] ?? 0))
  const total = values.reduce((a, b) => a + b, 0)
  if (total <= 0) return []
  return values.map((seconds, i) => ({ zone: i + 1, seconds, percent: Math.round((seconds / total) * 100) }))
}

export type SleepPhase = { key: 'deep' | 'light' | 'rem' | 'awake'; label: string; seconds: number }

export const PHASE_LABELS: Record<SleepPhase['key'], string> = {
  deep: 'Глубокий',
  light: 'Лёгкий',
  rem: 'REM',
  awake: 'Бодрствование',
}

export function sleepPhases(s: Pick<Sleep, 'deep_s' | 'light_s' | 'rem_s' | 'awake_s'>): SleepPhase[] {
  return (['deep', 'light', 'rem', 'awake'] as const)
    .map((key) => ({ key, label: PHASE_LABELS[key], seconds: s[`${key}_s`] ?? 0 }))
    .filter((p) => p.seconds > 0)
}

/** Ночи для графика фаз: часы с одним знаком, по возрастанию дня. */
export function sleepChartRows(nights: Sleep[]) {
  const hours = (s: number | null) => Math.round(((s ?? 0) / 3600) * 10) / 10
  return [...nights]
    .sort((a, b) => a.day.localeCompare(b.day))
    .map((n) => ({
      day: n.day,
      deep: hours(n.deep_s),
      light: hours(n.light_s),
      rem: hours(n.rem_s),
      awake: hours(n.awake_s),
      total: hours(n.duration_s),
      score: n.score,
    }))
}

/** Средняя длительность сна за ночи с данными, секунды. */
export function averageSleep(nights: Sleep[]): number | null {
  const values = nights.map((n) => n.duration_s).filter((v): v is number => typeof v === 'number' && v > 0)
  if (values.length === 0) return null
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length)
}

/** Ряд пульса [[секунды, уд/мин]] → точки графика в минутах. */
export function hrPoints(series: [number, number][] | null | undefined): { min: number; bpm: number }[] {
  if (!series) return []
  return series
    .filter(([t, bpm]) => Number.isFinite(t) && Number.isFinite(bpm) && bpm > 0)
    .map(([t, bpm]) => ({ min: Math.round((t / 60) * 10) / 10, bpm }))
}

export function formatDistance(meters: number | null | undefined): string | null {
  if (meters === null || meters === undefined || meters <= 0) return null
  return meters >= 1000 ? `${(meters / 1000).toFixed(2).replace('.', ',')} км` : `${Math.round(meters)} м`
}

/** Средний расход калорий за полные дни (сегодняшний ещё идёт — без него) и сравнение с нормой InBody:
 * каждый день — с нормой, что действовала в этот день (ступени по замерам). */
export function expenseSummary(
  points: { day: string; value: number }[],
  today: string,
  norms: SeriesPoint[],
): string | null {
  const full = points.filter((p) => p.day < today)
  if (full.length === 0) return null
  const mean = (values: number[]) => values.reduce((a, b) => a + b, 0) / values.length
  const n0 = (v: number) => formatNumber(v, { digits: 0 })
  const text = `В среднем ${n0(Math.round(mean(full.map((p) => p.value))))} ккал в день`
  const paired = full.flatMap((p) => {
    const norm = stepValueAt(norms, p.day)
    return norm === null ? [] : [{ value: p.value, norm }]
  })
  if (paired.length === 0) return text
  const diff = Math.round(mean(paired.map((p) => p.value - p.norm)))
  const used = new Set(paired.map((p) => p.norm))
  const only = used.size === 1 ? ` (${n0(paired[0]!.norm)})` : ''
  if (diff === 0) return `${text} — ровно норма InBody${only}`
  return `${text} — на ${n0(Math.abs(diff))} ${diff > 0 ? 'больше' : 'меньше'} нормы InBody${only}`
}
